import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
import {
  convertKesToUsd,
  formatUsdApprox,
  isRateFresh,
  RATE_REVALIDATE_INTERVAL_MS,
  readStoredCurrency,
  writeStoredCurrency,
  type Currency,
} from '../lib/currency';
import { fetchExchangeRate } from '../lib/api/exchangeRateApi';

/**
 * CURR-01: display-currency context (KES canonical, USD display-only).
 *
 * Mounted in BOTH app trees, because the three buyer surfaces are served by
 * two different renderers: App.tsx for the client-side SPA, and
 * src/pages/+Layout.tsx for the Vike SSR pages that answer a direct load.
 *
 * Supersedes the UX-034 version of this file, which converted at a hardcoded
 * 153 KES/USD. The live rate on the day this was written was 129.37 — an 18%
 * error, which never reached a buyer only because nothing ever rendered the
 * toggle. It is now read from `public.exchange_rates`, refreshed daily by the
 * exchange-rate-refresh Edge Function.
 *
 * SSR: the provider renders identically on server and first client paint —
 * currency KES, no rate, `isRateUsable` false. The stored preference is read
 * only AFTER mount. Reading localStorage during render would desync the
 * server's HTML from the client's first render and produce a hydration
 * mismatch; one frame of KES is the price of not having that.
 */

export type { Currency };

interface CurrencyContextType {
  /**
   * The EFFECTIVE display currency, not the raw preference. Falls back to KES
   * whenever the rate is unusable, so no consumer can render USD off a rate we
   * would not stand behind.
   */
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  /** KES per 1 USD. Null until loaded, or when the load failed. */
  rate: number | null;
  /** ISO timestamp of the last upstream refresh. */
  fetchedAt: string | null;
  /** Rate present, positive, and refreshed within the freshness window. */
  isRateUsable: boolean;
  /** KES primary figure. Always KES — this is the canonical price. */
  formatPrice: (priceKES: number, showCurrency?: boolean) => string;
  /**
   * The single conversion point. Returns `≈ $X,XXX` only when USD is selected,
   * the rate is usable, and the listing is actually priced in KES. Returns
   * null in every other case, which callers render as "no USD line".
   */
  usdApproxFor: (priceKES: number, listingCurrency?: string | null) => string | null;
}

const CurrencyContext = createContext<CurrencyContextType | null>(null);

interface CurrencyProviderProps {
  children: ReactNode;
}

const CurrencyStateProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  // Always KES for the server render and the first client render. The stored
  // preference is applied in the effect below.
  const [preference, setPreference] = useState<Currency>('KES');
  const [rate, setRate] = useState<number | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  // Bumped on every revalidation so freshness is re-evaluated as time passes,
  // not only when a fetch happens to succeed.
  const [tick, setTick] = useState(0);

  // Restore the saved preference after mount (client only).
  useEffect(() => {
    const stored = readStoredCurrency();
    if (stored) setPreference(stored);
  }, []);

  // Fetch the rate once per app load, not once per listing card — then keep it
  // honest for the lifetime of the mount.
  useEffect(() => {
    let cancelled = false;

    const revalidate = () => {
      // Re-evaluate freshness even if the refetch below fails, so a tab left
      // open past the 48h boundary falls back to KES instead of holding a
      // stale rate forever.
      setTick((t) => t + 1);

      fetchExchangeRate()
        .then((result) => {
          if (cancelled || !result) return;
          setRate(result.rate);
          setFetchedAt(result.fetchedAt);
        })
        .catch(() => {
          // Leave the previous values; freshness still ages them out.
        });
    };

    revalidate();

    const interval = setInterval(revalidate, RATE_REVALIDATE_INTERVAL_MS);

    // Native apps and background tabs get suspended rather than unmounted, so
    // a resume can jump across the boundary with no timer having fired.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const isRateUsable = useMemo(
    () => rate !== null && rate > 0 && fetchedAt !== null && isRateFresh(fetchedAt),
    // `tick` is a deliberate dependency: elapsed time changes this answer even
    // when rate and fetchedAt do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rate, fetchedAt, tick]
  );

  // Never hand out USD when we cannot back it with a fresh rate.
  const currency: Currency = isRateUsable ? preference : 'KES';

  const setCurrency = useCallback((next: Currency) => {
    setPreference(next);
    writeStoredCurrency(next);
  }, []);

  const formatPrice = useCallback((priceKES: number, showCurrency = true): string => {
    if (showCurrency) return formatCurrency(priceKES);
    return priceKES.toLocaleString();
  }, []);

  const usdApproxFor = useCallback(
    (priceKES: number, listingCurrency?: string | null): string | null => {
      if (currency !== 'USD' || rate === null) return null;

      // A listing priced in something other than KES cannot be converted with
      // a USD_KES rate. Better to show nothing than a number built on the
      // wrong pair.
      const code = (listingCurrency ?? 'KES').toUpperCase();
      if (code !== 'KES') return null;

      const usd = convertKesToUsd(priceKES, rate);
      if (usd === null) return null;

      return formatUsdApprox(usd);
    },
    [currency, rate]
  );

  const value = useMemo(
    () => ({ currency, setCurrency, rate, fetchedAt, isRateUsable, formatPrice, usdApproxFor }),
    [currency, setCurrency, rate, fetchedAt, isRateUsable, formatPrice, usdApproxFor]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

/**
 * Idempotent provider: if one is already mounted above, this renders children
 * unchanged instead of standing up a second, independent copy.
 *
 * Necessary because the two mount points legitimately nest. On a direct `/`
 * load the Vike layout provides one, then hydration swaps HomeSSR for App.tsx,
 * which provides another. Two live providers would double the rate request and
 * — worse — the inner one starts at KES and restores the saved preference a
 * beat later, so a buyer who had chosen USD would watch prices flip to KES and
 * back. App.tsx keeps its provider because native (src/native-entry.tsx) and
 * index.tsx mount App standalone with no Vike layout above it; there, App's
 * provider is the only one.
 */
export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const existing = useContext(CurrencyContext);

  if (existing) return <>{children}</>;

  return <CurrencyStateProvider>{children}</CurrencyStateProvider>;
};

/**
 * Returns null outside a provider rather than throwing.
 *
 * Deliberate: price rendering is shared by surfaces that sit inside the
 * provider and by ones that do not (agent dashboard, admin). A missing
 * provider must degrade to KES-only, never crash a page over a display
 * preference. Components use `useCurrencyOptional` and treat null as KES-only.
 */
export const useCurrencyOptional = (): CurrencyContextType | null => useContext(CurrencyContext);

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
